import { X } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useDialog } from "@src/hooks/dialog";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { contactSupport } from "@src/utils/contactSupport";

const PREFIX = "ScopedDialogProps";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: "unset",
  },
}));

export const ScopedDialog = () => {
  const dialog = useDialog("scoped");
  const navigate = useNavigate();

  const defaultYesAction = () => {
    void navigate(SETTINGS_ROUTES.Settings);
    dialog.close();
  };

  const defaultNoAction = () => {
    contactSupport();
  };

  const renderNoButton = (variant: "contained" | "outlined") => (
    <IgButton
      onClick={() => {
        if (!dialog.props?.noButtonAction) {
          defaultNoAction();
        } else {
          dialog.props.noButtonAction();
        }
      }}
      sx={{ letterSpacing: "0.5px", fontSize: "16px", fontWeight: 500 }}
      variant={variant}
    >
      {dialog.props?.noButtonText || "Contact Us"}
    </IgButton>
  );

  const renderYesButton = () => (
    <IgButton
      onClick={() => {
        if (!dialog.props?.yesButtonAction) {
          defaultYesAction();
        } else {
          dialog.props.yesButtonAction();
        }
      }}
      sx={{ letterSpacing: "0.5px", fontSize: "16px", fontWeight: 500 }}
      variant={"contained"}
    >
      {dialog.props?.yesButtonText || "View Plans"}
    </IgButton>
  );

  return (
    <StyledDialog
      open={dialog.isOpen}
      onClose={dialog.close}
      maxWidth={"xs"}
      slotProps={{
        paper: {
          sx: {
            display: !dialog.isOpen ? "none" : "flex",
            borderRadius: 15,
            width: "460px",
            height: "260px",
            justifyContent: "center",
            paddingBottom: "35px",
          },
        },
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <Grid
          sx={{
            paddingBottom: "0px",
            paddingRight: "12px",
            paddingTop: "12px",
          }}
          container
          direction={"row"}
          justifyContent={"flex-end"}
        >
          <IconButton onClick={dialog.close}>
            <X size={16} />
          </IconButton>
        </Grid>
        <DialogTitle id="dialogBox-title" className={classes.dialogTitle}>
          <Grid container direction={"column"}>
            <Grid>{dialog.props?.title}</Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ width: "346px" }}>
          <IgTypography>{dialog.props?.message}</IgTypography>
        </DialogContent>
        {dialog.props?.showButtons === true ? (
          <DialogActions>
            {renderNoButton("outlined")}
            {renderYesButton()}
          </DialogActions>
        ) : null}
        {dialog.props?.showButtons === false &&
          dialog.props?.showProductLimit === true && (
            <DialogActions>{renderNoButton("contained")}</DialogActions>
          )}
      </Grid>
    </StyledDialog>
  );
};
